import React, { useState, useEffect } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import Footer from "../../components/footer";
import Swal from "sweetalert2";
import { FaDatabase, FaPlay, FaExclamationTriangle, FaCheckCircle, FaTrashAlt, FaEnvelope, FaLock } from "react-icons/fa";

const DatabaseQueries = () => {
  const [sql, setSql] = useState("");
  const [queryMode, setQueryMode] = useState("read");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  
  // OTP properties
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    API.get("/me")
      .then((res) => {
        setCurrentUser(res);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  // Cooldown countdown timer
  useEffect(() => {
    let interval = null;
    if (cooldown > 0) {
      interval = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [cooldown]);

  const handleSendOtp = async () => {
    try {
      Swal.fire({ title: "Sending verification code...", didOpen: () => Swal.showLoading() });
      const res = await API.sendDatabaseQueryOtp();
      Swal.close();
      setOtpSent(true);
      setCooldown(60); // 60-second retry cooldown
      Swal.fire("Code Sent", res?.message || "A secure 6-digit code has been sent to your email address.", "success");
    } catch (err) {
      Swal.close();
      const errMsg = err?.response?.data?.message || err?.message || "Failed to send verification code.";
      Swal.fire("Error", errMsg, "error");
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.length !== 6) {
      Swal.fire("Warning", "Please enter a valid 6-digit OTP.", "warning");
      return;
    }
    setVerifying(true);
    try {
      await API.verifyDatabaseOtp(otp);
      setOtpVerified(true);
      Swal.fire("Success", "OTP verified! You can now execute your SQL script.", "success");
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "OTP verification failed.";
      Swal.fire("Error", errMsg, "error");
    } finally {
      setVerifying(false);
    }
  };

  const handleExecute = async (e) => {
    e.preventDefault();
    if (!sql.trim()) {
      Swal.fire("Warning", "Please write or paste your SQL script first.", "warning");
      return;
    }
    if (!otpVerified) {
      Swal.fire("Warning", "Please verify your 6-digit OTP first.", "warning");
      return;
    }

    const isUpdateMode = queryMode === "update";
    const isSchemaChange = isUpdateMode && /^\s*(?:--[^\n]*\n\s*)*(create\s+table|alter\s+table|create\s+(?:unique\s+)?index)\b/i.test(sql);
    const confirm = await Swal.fire({
      title: isSchemaChange ? "Confirm database schema change?" : isUpdateMode ? "Confirm database update?" : "Run read-only query?",
      text: isUpdateMode
        ? isSchemaChange
          ? "This will change the database structure and MySQL may commit it immediately. Verify table, column, index, and foreign-key names carefully."
          : "This INSERT, UPDATE, or DELETE will change database records. Review every WHERE clause carefully."
        : "Up to 500 result rows will be displayed. Database changes are not permitted.",
      icon: isUpdateMode ? "warning" : "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Execute",
      confirmButtonColor: isUpdateMode ? "#d33" : "#0891b2",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setLoading(true);
    setResult(null);

    try {
      const res = isUpdateMode
        ? await API.executeDatabaseUpdate(sql)
        : await API.executeDatabaseQuery(sql);
      setResult(isUpdateMode ? {
        success: true,
        mode: "update",
        message: res.message || "Database update completed successfully.",
        affectedRows: res.affected_rows ?? 0,
        statementCount: res.statement_count ?? 1,
        statementResults: res.statement_results || [],
        executionMs: res.execution_ms,
      } : {
        success: true,
        mode: "read",
        message: res.message || "Query executed successfully.",
        columns: res.columns || [],
        rows: res.rows || [],
        rowCount: res.row_count ?? (res.rows || []).length,
        truncated: Boolean(res.truncated),
        maxRows: res.max_rows || 500,
        executionMs: res.execution_ms,
      });
      setOtp(""); // Clear OTP on success
      setOtpVerified(false); // Reset verified state
      Swal.fire("Success", res.message || (isUpdateMode ? "Database updated successfully." : "Query executed successfully."), "success");
    } catch (err) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.message || "Failed to execute queries.";
      setResult({
        success: false,
        message: errMsg,
      });
      Swal.fire("Execution Error", errMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSql("");
    setResult(null);
  };

  // If user role is not admin, show Access Denied
  if (currentUser && currentUser.role !== "admin") {
    return (
      <div className="p-6 max-w-4xl mx-auto mt-10">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center shadow-md">
          <FaExclamationTriangle className="mx-auto text-red-500 text-5xl mb-4" />
          <h2 className="text-2xl font-bold text-red-800 mb-2">Access Denied</h2>
          <p className="text-red-600">
            Only administrators are authorized to execute raw database queries.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="p-6 max-w-6xl mx-auto min-h-screen">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-indigo-100 text-indigo-700 rounded-lg">
            <FaDatabase size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Database Administration</h1>
            <p className="text-sm text-slate-500">
              Inspect records or perform controlled data and schema operations. Admin access and email OTP are required.
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            <button type="button" onClick={() => { setQueryMode("read"); setSql(""); setResult(null); setOtpVerified(false); }} className={`px-5 py-2.5 rounded-lg text-sm font-bold transition ${queryMode === "read" ? "bg-cyan-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Read Database
            </button>
            <button type="button" onClick={() => { setQueryMode("update"); setSql(""); setResult(null); setOtpVerified(false); }} className={`px-5 py-2.5 rounded-lg text-sm font-bold transition ${queryMode === "update" ? "bg-rose-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Update Database
            </button>
          </div>
          {/* Editor (Left Side) */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
            <form onSubmit={handleExecute}>
              <div className="flex justify-between items-center mb-3">
                <label className="text-sm font-semibold text-slate-700">
                  {queryMode === "read" ? "Read-only SQL Query" : "Data / Schema Statements"}
                </label>
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-xs text-slate-400 hover:text-red-500 font-medium flex items-center gap-1 transition"
                >
                  <FaTrashAlt size={11} /> Clear Editor
                </button>
              </div>
              
              <textarea
                value={sql}
                onChange={(e) => setSql(e.target.value)}
                placeholder={queryMode === "read"
                  ? "-- Example:\nSELECT * FROM players WHERE district = 'Guntur' LIMIT 100;"
                  : "-- Data: INSERT, UPDATE, DELETE\n-- Schema: CREATE TABLE, ALTER TABLE, CREATE INDEX\n-- Do not mix data and schema statements in one batch.\nCREATE TABLE example_table (id BIGINT UNSIGNED NOT NULL AUTO_INCREMENT, PRIMARY KEY (id));"}
                className="w-full h-80 p-4 font-mono text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                style={{ tabSize: 4, color: "#f8fafc", backgroundColor: "#0f172a" }}
              />

              <div className="mt-4 flex flex-col md:flex-row items-center justify-between gap-4 bg-slate-50 p-4 rounded-lg border">
                {/* Security / OTP verification */}
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={cooldown > 0}
                    className={`px-4 py-2 bg-indigo-600 text-white font-semibold text-xs rounded-lg hover:bg-indigo-700 shadow-sm transition flex items-center gap-1.5 w-full sm:w-auto justify-center ${
                      cooldown > 0 ? "opacity-60 cursor-not-allowed bg-slate-400" : ""
                    }`}
                  >
                    <FaEnvelope size={12} /> {cooldown > 0 ? `Resend in ${cooldown}s` : otpSent ? "Resend OTP" : "Request Email OTP"}
                  </button>

                  <div className="relative w-full sm:w-44">
                    <FaLock className="absolute left-3 top-2.5 text-slate-400 text-xs" />
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value);
                        setOtpVerified(false);
                      }}
                      maxLength={6}
                      placeholder="Enter 6-digit OTP"
                      className="w-full pl-8 pr-3 py-1.5 border rounded-lg focus:outline-none focus:ring-1 focus:ring-indigo-500 text-center font-bold text-sm bg-white"
                      style={{ color: "#0f172a" }}
                    />
                  </div>

                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifying || otp.length !== 6 || otpVerified}
                    className={`px-4 py-2 font-semibold text-xs rounded-lg shadow-sm transition flex items-center gap-1.5 w-full sm:w-auto justify-center ${
                      otpVerified
                        ? "bg-emerald-600 text-white cursor-default"
                        : "bg-amber-600 hover:bg-amber-700 text-white disabled:bg-slate-300"
                    }`}
                  >
                    {otpVerified ? <FaCheckCircle /> : null} {otpVerified ? "Verified" : verifying ? "Verifying..." : "Verify OTP"}
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={loading || !otpVerified || !sql.trim()}
                  className={`px-5 py-2.5 text-white font-semibold text-sm rounded-lg shadow-sm transition flex items-center gap-2 w-full md:w-auto justify-center ${
                    loading || !otpVerified || !sql.trim()
                      ? "bg-slate-350 cursor-not-allowed opacity-50 bg-slate-400"
                      : queryMode === "update" ? "bg-rose-700 hover:bg-rose-800" : "bg-cyan-700 hover:bg-cyan-800"
                  }`}
                >
                  <FaPlay size={12} /> {loading ? "Running..." : queryMode === "update" ? "Execute Update" : "Run Query"}
                </button>
              </div>
            </form>
          </div>

        {/* Results Panel & Info (Right Side) */}
        <div className="space-y-6">
          {/* Results Output */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex flex-col min-h-[220px]">
            <h2 className="text-sm font-semibold text-slate-700 mb-3 pb-2 border-b">
              Query Results
            </h2>
            
            {result === null ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center text-slate-400 p-4">
                <FaDatabase size={36} className="mb-2 opacity-50" />
                <p className="text-xs">No query has been executed yet.</p>
                <p className="text-[10px] text-slate-400 mt-1">
                  Enter a SELECT, SHOW, DESCRIBE, DESC, or EXPLAIN query, verify the OTP, then run it.
                </p>
              </div>
            ) : result.success && result.mode === "update" ? (
              <div className="flex-1 p-5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-800">
                <div className="flex items-center gap-2 mb-2 font-bold text-sm"><FaCheckCircle /> Database Updated Successfully</div>
                <p className="text-sm">{result.message}</p>
                <p className="text-xs mt-2 text-emerald-700">Statements: <strong>{result.statementCount}</strong> · Affected rows: <strong>{result.affectedRows}</strong>{result.executionMs !== undefined ? ` · ${result.executionMs} ms` : ""}</p>
                {result.statementResults.length > 1 && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {result.statementResults.map((item) => (
                      <div key={item.statement} className="rounded border border-emerald-200 bg-white px-3 py-2 text-xs">
                        Statement {item.statement}: <strong>{item.affected_rows}</strong> row(s)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : result.success ? (
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 font-bold text-sm">
                  <FaCheckCircle className="text-emerald-500" />
                  {result.message}
                </div>
                <p className="text-[11px] text-slate-500 mb-3">
                  {result.executionMs !== undefined ? `${result.executionMs} ms · ` : ""}{result.rowCount} displayed row(s)
                  {result.truncated ? ` · Limited to the first ${result.maxRows} rows` : ""}
                </p>
                <div className="overflow-auto max-h-[65vh] border border-slate-200 rounded-lg">
                  <table className="min-w-full text-xs bg-white">
                    <thead className="sticky top-0 z-10 bg-slate-100 text-slate-700">
                      <tr>
                        <th className="border-b border-r border-slate-200 px-3 py-2 text-right">#</th>
                        {result.columns.map((column) => (
                          <th key={column} className="border-b border-r border-slate-200 px-3 py-2 text-left whitespace-nowrap">{column}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.length ? result.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} className="odd:bg-white even:bg-slate-50 hover:bg-cyan-50">
                          <td className="border-b border-r border-slate-100 px-3 py-2 text-right text-slate-400">{rowIndex + 1}</td>
                          {result.columns.map((column) => (
                            <td key={column} className="border-b border-r border-slate-100 px-3 py-2 font-mono whitespace-pre-wrap min-w-[100px] max-w-[360px] break-words">
                              {row[column] === null ? <span className="italic text-slate-400">NULL</span> : typeof row[column] === "object" ? JSON.stringify(row[column]) : String(row[column])}
                            </td>
                          ))}
                        </tr>
                      )) : (
                        <tr><td colSpan={Math.max(result.columns.length + 1, 1)} className="p-6 text-center text-slate-500">Query returned no rows.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="flex-1 p-4 bg-rose-50 border border-rose-200 rounded-lg text-rose-800">
                <div className="flex items-center gap-2 mb-2 font-bold text-sm">
                  <FaExclamationTriangle className="text-rose-500" />
                  Database Exception Error
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <p className="text-xs font-mono whitespace-pre-wrap break-words">
                    {result.message}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Security details card */}
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-5 text-slate-600">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">
              Why Email OTP Verification?
            </h3>
            <p className="text-xs leading-relaxed">
              Database operations require an administrator email OTP. UPDATE and DELETE require WHERE clauses. CREATE TABLE, ALTER TABLE, and CREATE INDEX are permitted; destructive DROP and TRUNCATE commands remain blocked.
            </p>
          </div>
        </div>
      </div>
      </div>
      <Footer />
    </>
  );
};

export default DatabaseQueries;
